import type { TeamsSendRequest } from "./api";
import { useQueryClient } from "@tanstack/react-query";
import {
	queryOptions,
	useMutation,
	useQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import {
	createAndSendMessage,
	createAndSendMultipleMessages,
	getAllStaffOids,
	getAllStudentOids,
	sendTeamsMessage,
} from "./api";
import { useUser } from "@features/user-account/queries";
import { usePortalModeStore } from "@stores/usePortalModeStore";
import toast from "react-hot-toast";
import { useId } from "@mantine/hooks";

export function useSendTeamsMessage() {
	const me = useUser().data?.id;
	const uuid = useId();

	return useMutation({
		mutationFn: async ({
			recipient,
			content,
		}: {
			recipient: string;
			content: string;
		}) => {
			if (!me) {
				throw new Error(
					"Unable to send Teams message without the current user id.",
				);
			}

			return createAndSendMessage(recipient, content);
		},
		onMutate() {
			const toastId = toast.loading("Sending message...", { id: uuid });
			return { toastId };
		},
		onSuccess(data) {
			if (data.failedCount)
				toast.error(
					"Some deliveries could not be confirmed. Check Teams before resending.",
					{ id: uuid },
				);
			else toast.success("Message sent", { id: uuid });
			for (const warning of data.warnings) toast.error(warning);
		},
		onError() {
			toast.error("Failed to send message", { id: uuid });
		},
	});
}

export function useSendMultipleTeamsMessages() {
	const me = useUser().data?.id;

	const uuid = useId();

	return useMutation({
		mutationFn: async ({
			recipients,
			content,
		}: {
			recipients: string[];
			content: string;
		}) => {
			if (!me) {
				throw new Error(
					"Unable to send Teams messages without the current user id.",
				);
			}

			return createAndSendMultipleMessages(recipients, content);
		},
		onMutate() {
			const toastId = toast.loading("Sending message...", { id: uuid });
			return { toastId };
		},
		onSuccess(data) {
			if (data.failedCount)
				toast.error(
					"Some deliveries could not be confirmed. Check Teams before resending.",
					{ id: uuid },
				);
			else toast.success("Message sent", { id: uuid });
			for (const warning of data.warnings) toast.error(warning);
		},
		onError() {
			toast.error("Failed to send message", { id: uuid });
		},
	});
}

export const allStaffOidsQueryOptions = () =>
	queryOptions({
		queryKey: ["staffOids"],
		queryFn: () => getAllStaffOids(),
		enabled: usePortalModeStore.getState().hostMode === "staff",
		staleTime: Infinity,
	});

export function useStaffOids() {
	return useQuery(allStaffOidsQueryOptions());
}

export function useStaffOidsSuspense() {
	return useSuspenseQuery(allStaffOidsQueryOptions());
}

export const allStudentOidsQueryOptions = () =>
	queryOptions({
		queryKey: ["communicationStudentDirectory"],
		queryFn: () => getAllStudentOids(),
		enabled: usePortalModeStore.getState().hostMode === "staff",
		staleTime: Infinity,
	});

export function useStudentOids() {
	return useQuery(allStudentOidsQueryOptions());
}

export function useStudentOidsSuspense() {
	return useSuspenseQuery(allStudentOidsQueryOptions());
}

export function useSendTeamsMessageProxy() {
	const client = useQueryClient();

	const uuid = useId();

	return useMutation({
		mutationFn: (params: TeamsSendRequest) => sendTeamsMessage(params),
		onMutate() {
			const toastId = toast.loading("Sending message...", { id: uuid });
			return { toastId };
		},
		async onSuccess(data) {
			if (data.failedCount > 0)
				toast.error(
					`${data.sentCount} chats sent; ${data.failedCount} could not be confirmed. Check Teams before sending again.`,
					{ id: uuid },
				);
			else toast.success("Message sent", { id: uuid });
			for (const warning of data.warnings)
				toast.error(warning, { duration: 10000 });
			await Promise.all([
				client.invalidateQueries({ queryKey: ["communications", "my-sent"] }),
				client.invalidateQueries({ queryKey: ["students"] }),
				client.invalidateQueries({ queryKey: ["conversations"] }),
			]);
		},
		onError() {
			toast.error("Failed to send message", { id: uuid });
		},
	});
}
